import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useStoreConfig } from '../contexts/StoreConfigContext';

export const DynamicMetaTags: React.FC = () => {
    const { config } = useStoreConfig();

    const siteName = config.companyName || 'Cosmo Cart';
    const title = `${config.companyName} - Online Shop`;
    const description = config.footerDescription || 'Shop quality products online';
    const logo = config.appLogo;
    const primaryColor = config.primaryColor;
    const url = typeof window !== 'undefined' ? window.location.href : '';

    // Use dedicated sharing images if available, otherwise fall back to logo
    const sharingSquare = config.sharingImageSquare || logo;
    const sharingLandscape = config.sharingImageLandscape || sharingSquare;

    // Determine image dimensions based on which image we're using
    const ogImageWidth = config.sharingImageLandscape ? 1200 : 1024;
    const ogImageHeight = config.sharingImageLandscape ? 630 : 1024;
    const ogImage = config.sharingImageLandscape || sharingSquare;

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{title}</title>
            <meta name='description' content={description} />
            <meta name='theme-color' content={primaryColor} />
            
            {/* Favicon */}
            <link rel='icon' href={logo} />
            <link rel='apple-touch-icon' href={logo} />
            
            {/* Open Graph / Facebook / WhatsApp / LinkedIn */}
            <meta property='og:type' content='website' />
            <meta property='og:url' content={url} />
            <meta property='og:title' content={title} />
            <meta property='og:description' content={description} />
            <meta property='og:site_name' content={siteName} />
            <meta property='og:locale' content='en_US' />
            
            {/* Primary OG Image - Landscape for Facebook/WhatsApp/LinkedIn */}
            <meta property='og:image' content={ogImage} />
            <meta property='og:image:secure_url' content={ogImage} />
            <meta property='og:image:type' content='image/jpeg' />
            <meta property='og:image:width' content={ogImageWidth.toString()} />
            <meta property='og:image:height' content={ogImageHeight.toString()} />
            <meta property='og:image:alt' content={`${config.companyName} - Online Shop`} />

            {/* Secondary OG Image - Square as fallback */}
            {sharingSquare !== ogImage && (
                <>
                    <meta property='og:image' content={sharingSquare} />
                    <meta property='og:image:secure_url' content={sharingSquare} />
                    <meta property='og:image:type' content='image/jpeg' />
                    <meta property='og:image:width' content='1024' />
                    <meta property='og:image:height' content='1024' />
                    <meta property='og:image:alt' content={`${config.companyName} Logo`} />
                </>
            )}

            {/* Twitter - Use square image */}
            <meta property='twitter:card' content='summary' />
            <meta property='twitter:url' content={url} />
            <meta property='twitter:title' content={title} />
            <meta property='twitter:description' content={description} />
            <meta property='twitter:image' content={sharingSquare} />
            
            {/* PWA / Mobile */}
            <meta name='apple-mobile-web-app-title' content={siteName} />
            <meta name='application-name' content={siteName} />
            <meta name='apple-mobile-web-app-capable' content='yes' />
            <meta name='mobile-web-app-capable' content='yes' />
        </Helmet>
    );
};

